/**
 * GameStatsChartModal - Modal popup displaying team game history for game props
 * Mobile: slides up as bottom sheet. Desktop: centered modal.
 * Controls: team perspective toggle, slider, over/under toggle, +/- buttons, reset
 */

import type { LeagueKey } from '@parlay-picker/shared/league';
import { hasPlayerProps } from '@parlay-picker/shared/league';
import { getTeamInfo } from '@parlay-picker/shared/teams';
import { cn } from '@utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { ClipboardList, X } from 'lucide-react';
import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { RosterIntelPanel } from './RosterIntelPanel';
import TeamStatsBarChart, { type TeamGameData } from './TeamStatsBarChart';

interface GameStatsChartModalProps {
  onClose: () => void;
  teamName: string;
  prop: string;
  recentGames?: TeamGameData[];
  h2hGames?: TeamGameData[];
  propLine?: number;
  propType: 'spread' | 'total' | 'moneyline';
  opponentAbbr?: string;
  overUnder?: string | null;
  opponentTeamName?: string;
  opponentTeamRecentGames?: TeamGameData[];
  odds?: string;
  /** Market key for period-specific charts (e.g., 'totals_h1', 'spreads_q3') */
  marketKey?: string;
  /** League key — used to disable player-prop features (e.g., roster intel) for college leagues */
  leagueKey: LeagueKey;
}

// Slider ranges per prop type [min, max]
const LINE_RANGES: Record<string, [number, number]> = {
  spread: [-40, 40],
  total: [180, 260],
  moneyline: [0, 0],
};

// Default lines per prop type
const DEFAULT_LINES: Record<string, number> = {
  spread: -5.5,
  total: 220.5,
  moneyline: 0,
};

// Prop type labels
const PROP_TYPE_LABELS: Record<string, string> = {
  spread: 'Spread',
  total: 'Total',
  moneyline: 'Moneyline',
};

function getTeamAbbrLocal(teamName: string | undefined, leagueKey?: LeagueKey): string | undefined {
  if (!teamName || !leagueKey) return undefined;
  return getTeamInfo(teamName, leagueKey).abbr;
}

function getTeamLogoLocal(teamName: string | undefined, leagueKey?: LeagueKey): string | undefined {
  if (!teamName || !leagueKey) return undefined;
  const logo = getTeamInfo(teamName, leagueKey).logo;
  return logo || undefined;
}

// TeamLogoFallback imported from shared component
import { TeamLogoFallback } from './TeamLogo';

/** Compute average game stat from recent games, rounded to nearest 0.5 */
function computeAvgLine(
  games: TeamGameData[] | undefined,
  propType: 'spread' | 'total' | 'moneyline',
): number | null {
  if (!games || games.length === 0 || propType === 'moneyline') return null;
  const getValue = (g: TeamGameData) => (propType === 'total' ? g.gameTotal : g.margin);
  const sum = games.reduce((acc, g) => acc + getValue(g), 0);
  const avg = sum / games.length;
  return Math.round(avg * 2) / 2;
}

function formatLine(line: number, propType: string): string {
  if (propType === 'spread' && line > 0) return `+${line}`;
  return line.toString();
}

export default function GameStatsChartModal({
  onClose,
  teamName,
  prop,
  recentGames,
  h2hGames,
  propLine: initialPropLine,
  propType: initialPropType,
  opponentAbbr,
  overUnder: initialOverUnder,
  opponentTeamName,
  opponentTeamRecentGames,
  marketKey,
  leagueKey,
}: GameStatsChartModalProps): ReactElement | null {
  const [propType, setPropType] = useState<'spread' | 'total' | 'moneyline'>(initialPropType);
  const [selectedLine, setSelectedLine] = useState<number>(
    initialPropLine ?? computeAvgLine(recentGames, propType) ?? DEFAULT_LINES[propType],
  );
  const [selectedOverUnder, setSelectedOverUnder] = useState<'over' | 'under'>(
    initialOverUnder?.toLowerCase() === 'under' ? 'under' : 'over',
  );
  const [selectedTeam, setSelectedTeam] = useState<'primary' | 'opponent'>('primary');
  const [logoError, setLogoError] = useState(false);
  const [showRosterIntel, setShowRosterIntel] = useState(false);

  // Reset line when prop type changes (skip when propType hasn't actually
  // changed so the initial line from the parent isn't wiped on mount)
  const prevPropTypeRef = useRef(propType);
  useEffect(() => {
    if (prevPropTypeRef.current === propType) return;
    prevPropTypeRef.current = propType;
    setSelectedLine(computeAvgLine(recentGames, propType) ?? DEFAULT_LINES[propType]);
    if (propType === 'total') setSelectedOverUnder('over');
  }, [propType, recentGames]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = parseFloat(e.target.value);
    setSelectedLine(Math.round(value * 2) / 2);
  }, []);

  const [minLine, maxLine] = LINE_RANGES[propType] || LINE_RANGES.total;

  const [lineInput, setLineInput] = useState(
    formatLine(
      initialPropLine ??
        computeAvgLine(recentGames, initialPropType) ??
        DEFAULT_LINES[initialPropType],
      initialPropType,
    ),
  );

  // Keep input in sync when selectedLine changes (from slider, +/-, reset)
  useEffect(() => {
    setLineInput(formatLine(selectedLine, propType));
  }, [selectedLine, propType]);

  const handleLineInputSubmit = useCallback(() => {
    const parsed = parseFloat(lineInput);
    if (!Number.isNaN(parsed)) {
      const clamped = Math.max(minLine, Math.min(maxLine, Math.round(parsed * 2) / 2));
      setSelectedLine(clamped);
    } else {
      setLineInput(formatLine(selectedLine, propType));
    }
  }, [lineInput, minLine, maxLine, selectedLine, propType]);
  const showSlider = propType !== 'moneyline';
  const showOverUnder = propType === 'total';

  const canToggleTeam =
    opponentTeamName && opponentTeamRecentGames && opponentTeamRecentGames.length > 0;

  const invertH2hGames = (games: TeamGameData[] | undefined): TeamGameData[] | undefined => {
    if (!games) return undefined;
    return games.map((game) => ({
      ...game,
      margin: -game.margin,
      teamScore: game.opponentScore,
      opponentScore: game.teamScore,
      won: !game.won,
      isHome: !game.isHome,
    }));
  };

  const currentTeamName = selectedTeam === 'primary' ? teamName : opponentTeamName || teamName;
  const currentRecentGames = selectedTeam === 'primary' ? recentGames : opponentTeamRecentGames;
  const currentH2hGames = selectedTeam === 'primary' ? h2hGames : invertH2hGames(h2hGames);
  const currentOpponentAbbr =
    selectedTeam === 'primary' ? opponentAbbr : getTeamAbbrLocal(teamName, leagueKey);

  const primaryAbbr = getTeamAbbrLocal(teamName, leagueKey) || 'TM1';
  const opponentTeamAbbr = getTeamAbbrLocal(opponentTeamName, leagueKey) || opponentAbbr || 'TM2';

  const currentTeamLogo = getTeamLogoLocal(currentTeamName, leagueKey);
  const currentTeamAbbrForFallback = selectedTeam === 'primary' ? primaryAbbr : opponentTeamAbbr;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-60 flex items-end sm:items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal — bottom sheet on mobile, centered on desktop */}
        <motion.div
          initial={{ y: '100%', opacity: 0.8 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 35 }}
          className={cn(
            'relative bg-theme-surface border border-theme-border shadow-2xl w-full',
            'rounded-t-2xl sm:rounded-xl',
            'sm:max-w-lg sm:mx-4',
            'max-h-[90vh] overflow-y-auto overscroll-contain',
          )}
        >
          {/* Drag handle (mobile only) */}
          <div className="flex justify-center pt-2 pb-0 sm:hidden">
            <div className="w-10 h-1 rounded-full bg-theme-surface-active" />
          </div>

          <div className="p-4 sm:p-5 space-y-4">
            {/* Header with team logo */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="shrink-0">
                  {currentTeamLogo && !logoError ? (
                    <img
                      src={currentTeamLogo}
                      alt={currentTeamName}
                      className="w-10 h-10 object-contain"
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <TeamLogoFallback abbr={currentTeamAbbrForFallback} />
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-theme-text truncate">
                    {currentTeamName}
                  </h3>
                  <p className="text-xs sm:text-sm text-theme-text-tertiary truncate">{prop}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {primaryAbbr && hasPlayerProps(leagueKey) && (
                  <button
                    type="button"
                    onClick={() => setShowRosterIntel(true)}
                    className="p-2 rounded-lg hover:bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-text transition"
                    title="Roster intel"
                  >
                    <ClipboardList className="w-5 h-5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-text transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Team Perspective Toggle — animated */}
            {canToggleTeam && (
              <div className="relative flex bg-theme-bg/50 rounded-lg p-0.5">
                {(['primary', 'opponent'] as const).map((team) => {
                  const isActive = selectedTeam === team;
                  const label = team === 'primary' ? primaryAbbr : opponentTeamAbbr;
                  return (
                    <button
                      type="button"
                      key={team}
                      onClick={() => {
                        setSelectedTeam(team);
                        setLogoError(false);
                      }}
                      className={cn(
                        'relative flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors z-10',
                        isActive
                          ? 'text-theme-on-primary-fill'
                          : 'text-theme-text-tertiary hover:text-theme-text',
                      )}
                    >
                      {isActive && (
                        <motion.div
                          layoutId="activeTeamToggle"
                          className="absolute inset-0 bg-theme-primary-fill rounded-md"
                          transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 30,
                          }}
                        />
                      )}
                      <span className="relative">{label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Prop Type Selector */}
            <div className="relative flex bg-theme-surface rounded-lg p-1">
              {(['spread', 'total', 'moneyline'] as const).map((pt) => {
                const isActive = propType === pt;
                return (
                  <button
                    type="button"
                    key={pt}
                    onClick={() => setPropType(pt)}
                    className={cn(
                      'relative flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors z-10 capitalize',
                      isActive
                        ? 'text-theme-on-primary-fill'
                        : 'text-theme-text-tertiary hover:text-theme-text',
                    )}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activePropType"
                        className="absolute inset-0 bg-theme-primary-fill rounded-md"
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      />
                    )}
                    <span className="relative">{PROP_TYPE_LABELS[pt]}</span>
                  </button>
                );
              })}
            </div>

            {/* Controls — only show for spread and total */}
            {showSlider && (
              <div className="space-y-2">
                {/* Over/Under Toggle + Line +/- Controls Row */}
                <div className="flex items-center justify-between gap-3">
                  {showOverUnder ? (
                    <div className="flex bg-theme-surface-hover rounded-lg p-0.5">
                      <button
                        type="button"
                        onClick={() => setSelectedOverUnder('over')}
                        className={cn(
                          'px-3 py-1.5 text-xs font-medium rounded-md transition',
                          selectedOverUnder === 'over'
                            ? 'bg-theme-success text-theme-text'
                            : 'text-theme-text-tertiary hover:text-theme-text',
                        )}
                      >
                        Over
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedOverUnder('under')}
                        className={cn(
                          'px-3 py-1.5 text-xs font-medium rounded-md transition',
                          selectedOverUnder === 'under'
                            ? 'bg-theme-danger text-theme-text'
                            : 'text-theme-text-tertiary hover:text-theme-text',
                        )}
                      >
                        Under
                      </button>
                    </div>
                  ) : (
                    <div />
                  )}

                  {/* Line +/- Controls + Reset */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedLine(Math.max(minLine, selectedLine - 0.5))}
                      disabled={selectedLine <= minLine}
                      className="w-8 h-8 flex items-center justify-center bg-theme-surface-hover text-theme-text rounded-md hover:bg-theme-surface-active disabled:opacity-40 disabled:cursor-not-allowed transition font-medium"
                    >
                      −
                    </button>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="bg-theme-primary-fill text-theme-on-primary-fill px-3 py-1.5 rounded-md text-sm font-bold min-w-13 text-center tabular-nums w-16 outline-none focus:ring-2 focus:ring-theme-primary/50"
                      value={lineInput}
                      onChange={(e) => setLineInput(e.target.value)}
                      onBlur={handleLineInputSubmit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleLineInputSubmit();
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setSelectedLine(Math.min(maxLine, selectedLine + 0.5))}
                      disabled={selectedLine >= maxLine}
                      className="w-8 h-8 flex items-center justify-center bg-theme-surface-hover text-theme-text rounded-md hover:bg-theme-surface-active disabled:opacity-40 disabled:cursor-not-allowed transition font-medium"
                    >
                      +
                    </button>

                    {/* Reset Button */}
                    {initialPropLine != null &&
                      (() => {
                        const initialDirection =
                          initialOverUnder?.toLowerCase() === 'under' ? 'under' : 'over';
                        const lineChanged = selectedLine !== initialPropLine;
                        const directionChanged =
                          showOverUnder && selectedOverUnder !== initialDirection;
                        if (!lineChanged && !directionChanged) return null;
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedLine(initialPropLine);
                              setSelectedOverUnder(initialDirection);
                            }}
                            className="px-2 py-1.5 text-xs bg-theme-surface-hover text-theme-text-secondary rounded-md hover:bg-theme-surface-active transition"
                            title={`Reset to ${showOverUnder ? initialOverUnder || 'Over' : ''} ${formatLine(initialPropLine, propType)}`}
                          >
                            Reset
                          </button>
                        );
                      })()}
                  </div>
                </div>

                {/* Compact slider for quick line jumps */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-theme-text-tertiary tabular-nums">
                    {formatLine(minLine, propType)}
                  </span>
                  <input
                    type="range"
                    min={minLine}
                    max={maxLine}
                    step={0.5}
                    value={selectedLine}
                    onChange={handleSliderChange}
                    className="flex-1 h-1.5 bg-theme-surface-hover rounded-full appearance-none cursor-pointer accent-theme-primary"
                  />
                  <span className="text-[10px] text-theme-text-tertiary tabular-nums">
                    {formatLine(maxLine, propType)}
                  </span>
                </div>
              </div>
            )}

            {/* Chart */}
            <TeamStatsBarChart
              recentGames={currentRecentGames}
              h2hGames={currentH2hGames}
              propLine={selectedLine}
              propType={propType}
              teamName={currentTeamName}
              opponentAbbr={currentOpponentAbbr}
              overUnder={showOverUnder ? selectedOverUnder : null}
              isCustomLine={initialPropLine != null && selectedLine !== initialPropLine}
              marketKey={marketKey}
            />

            {/* Info for moneyline */}
            {propType === 'moneyline' && (
              <p className="text-xs text-theme-text-tertiary text-center">
                Chart shows team's win/loss record with point margin
              </p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Roster Intel Panel overlay */}
      {showRosterIntel && primaryAbbr && (
        <RosterIntelPanel
          teamAbbr={selectedTeam === 'primary' ? primaryAbbr : opponentTeamAbbr}
          opponentAbbr={selectedTeam === 'primary' ? opponentAbbr : primaryAbbr}
          leagueKey={leagueKey}
          onClose={() => setShowRosterIntel(false)}
        />
      )}
    </AnimatePresence>
  );
}
